import { Stack } from "@mui/material";

import { styled } from "@mui/material/styles";

import { useFormatDurationShort } from "hooks/useFormat";
import { useFormatNumber } from "hooks/useFormatNumber";
import { NightReportMetric } from "interfaces/nightReport.interface";

import { AirCard } from "./cards/AirCard";
import { MovementCard } from "./cards/MovementCard";
import { OutOfBedCard } from "./cards/OutOfBedCard";
import { SleepScoreCard } from "./cards/SleepScoreCard";
import { SleepTimeCard } from "./cards/SleepTimeCard";
import { SummaryCard } from "./cards/SummaryCard";
import { TemperatureCard } from "./cards/TemperatureCard";
import { RespirationCard } from "./cards/RespirationCard";
import { DeviationMetric } from "./components/DeviationText";

const SummaryWrapper = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    "> *": {
      width: "100%",
    },
  },
}));

const StatsWrapper = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    "> *": {
      width: "100%",
    },
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
}));

type Session = {
  timeAsleep: number;
  sleepScoreStandard: number;
  numberOfTimesNoPresence: number;
  epochsWithMovementPct: number;
  airQualityFilteredMean?: number | null;
  airHumidityFilteredMean?: number | null;
  temperatureFilteredMean?: number | null;
  respirationRateMean: number | null;
};

type SleepOverviewSectionProps = {
  totalSleepTimeMetric: NightReportMetric | null;
  sleepScoreMetric: NightReportMetric | null;
  timesOutOfBedMetric: NightReportMetric | null;
  movementMetric: NightReportMetric | null;
  respirationMetric: NightReportMetric | null;
  deviations: DeviationMetric[] | null;
  session: Session;
};

export function SleepOverviewSection({
  totalSleepTimeMetric,
  sleepScoreMetric,
  deviations,
  timesOutOfBedMetric,
  movementMetric,
  respirationMetric,
  session,
}: SleepOverviewSectionProps) {
  const formatDuration = useFormatDurationShort();
  const formatNumber = useFormatNumber();

  return (
    <Stack sx={{ gap: 2 }}>
      <SummaryWrapper direction={"row"}>
        <SummaryCard deviations={deviations} />
      </SummaryWrapper>
      <StatsWrapper direction={"row"}>
        <SleepTimeCard
          totalSleepTime={formatDuration(session.timeAsleep)}
          totalSleepTimeMetric={totalSleepTimeMetric}
        />
        <SleepScoreCard
          sleepScore={formatNumber(session.sleepScoreStandard)}
          sleepScoreMetric={sleepScoreMetric}
        />
        <RespirationCard
          respiration={
            session.respirationRateMean
              ? formatNumber(session.respirationRateMean, {
                  decimals: 1,
                })
              : null
          }
          respirationMetric={respirationMetric}
        />
        <OutOfBedCard
          timesOutOfBed={formatNumber(session.numberOfTimesNoPresence)}
          timesOutOfBedMetric={timesOutOfBedMetric}
        />
        <MovementCard
          movement={session.epochsWithMovementPct}
          movementMetric={movementMetric}
        />
        <TemperatureCard
          temperature={
            session.temperatureFilteredMean == null
              ? null
              : formatNumber(session.temperatureFilteredMean)
          }
        />
        <AirCard
          airQuality={session.airQualityFilteredMean}
          humidity={session.airHumidityFilteredMean}
        />
      </StatsWrapper>
    </Stack>
  );
}
